/**
 * 训练趋势数据查询（阶段 6：从 IndexedDB 提取原生数据，聚合交给 @jilian/core 纯函数）。
 *
 * 只做读取与形状整理，不做任何统计/口径换算（规则层负责 —— 保证与单测一致）。
 */

import { db } from "../db/db";
import { buildExerciseSeries, type ExercisePoint } from "@jilian/core";

/** 有完成组记录的动作（用于趋势页动作选择器），附最近一次训练日期。 */
export async function listExercisesWithHistory(excludeSessionId?: string): Promise<Array<{ exerciseId: string; nameZh: string; lastDate: string }>> {
  const items = await db.exerciseItems.toArray();
  const sets = await db.sets.toArray();
  const sessions = await db.sessions.toArray();
  const sessionById = new Map(sessions.map((s) => [s.id, s]));
  const byExercise = new Map<string, { nameZh: string; lastDate: string }>();

  for (const item of items) {
    if (excludeSessionId && item.sessionId === excludeSessionId) continue;
    const session = sessionById.get(item.sessionId);
    if (!session || (session.status !== "completed" && session.status !== "ended_early")) continue;
    const hasSet = sets.some((s) => s.workoutExerciseId === item.id && s.status === "completed");
    if (!hasSet) continue;
    const prev = byExercise.get(item.exerciseId);
    const date = session.localDateAtStart;
    if (!prev || date > prev.lastDate) {
      byExercise.set(item.exerciseId, { nameZh: item.exerciseId, lastDate: date });
    }
  }
  // 用动作库补中文名
  const exercises = await db.exercises.toArray();
  const nameById = new Map(exercises.map((e) => [e.id, e.nameZh]));
  return [...byExercise.entries()]
    .map(([exerciseId, v]) => ({ exerciseId, nameZh: nameById.get(exerciseId) ?? exerciseId, lastDate: v.lastDate }))
    .sort((a, b) => (a.lastDate < b.lastDate ? 1 : -1));
}

/** 某动作的表现序列（同口径：取该动作「最近一次完成组记录」的口径；聚合规则见 core/trends）。 */
export async function loadExerciseSeries(exerciseId: string): Promise<{ series: ExercisePoint[]; scope: string }> {
  const items = await db.exerciseItems.where("exerciseId").equals(exerciseId).toArray();
  const itemIds = items.map((i) => i.id);
  if (itemIds.length === 0) return { series: [], scope: "" };
  const sets = await db.sets.where("workoutExerciseId").anyOf(itemIds).toArray();
  const sessions = await db.sessions.toArray();
  const sessionById = new Map(sessions.map((s) => [s.id, s]));
  const itemById = new Map(items.map((i) => [i.id, i]));

  // 取该动作最近一次完成组的口径作为「趋势口径」（同口径铁律）
  const completed = sets
    .filter((s) => s.status === "completed" && s.actualWeightKg != null && s.reps != null && s.rir != null)
    .sort((a, b) => ((b.completedAt ?? "") < (a.completedAt ?? "") ? -1 : 1));
  if (completed.length === 0) return { series: [], scope: "" };
  const scope = completed[0]!.weightUnitScope;

  const ordered = [...sets].sort((a, b) => ((a.completedAt ?? a.updatedAt) > (b.completedAt ?? b.updatedAt) ? 1 : -1));
  const series = buildExerciseSeries({
    sets: ordered,
    dayBySet: ordered.map((s) => {
      const item = itemById.get(s.workoutExerciseId);
      const session = item ? sessionById.get(item.sessionId) : undefined;
      return session?.localDateAtStart ?? "";
    }),
    sessionIdBySet: ordered.map((s) => itemById.get(s.workoutExerciseId)?.sessionId ?? ""),
    weightUnitScope: scope,
  });
  return { series, scope };
}